import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Controller } from "react-hook-form";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { FieldError } from "../FieldError";

export const MultiSelect = ({ control, name, options, step, disabled }) => {
  const [open, setOpen] = React.useState(false);

  // quick map to resolve labels for selected values
  const labelByValue = React.useMemo(
    () => Object.fromEntries(options.map(o => [o.value, o.label])),
    [options]
  );

  return (
    <div className="space-y-3">
      <Controller
        control={control}
        name={name}
        defaultValue={[]}
        render={({ field }) => {
          const selected = field.value ?? [];
  
          const toggle = (val) => {
            const set = new Set(selected);
            set.has(val) ? set.delete(val) : set.add(val);
            field.onChange(Array.from(set));
          };
  
          const remove = (val) => {
            field.onChange(selected.filter(s => s !== val));
          };
  
          return (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={disabled}
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between flex-wrap h-auto min-h-[40px] py-2"
                >
                  <div className="flex flex-wrap gap-2">
                    {selected.length === 0 ? (
                      <span className="text-gray-400">Select options…</span>
                    ) : (
                      selected.map((val) => (
                        <Badge key={val} variant="secondary" className="flex items-center gap-1 px-2 py-1">
                          {labelByValue[val] ?? val}
                          <button
                            type="button"
                            data-badge-remove="true"
                            className="inline-flex"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              remove(val);
                            }}
                          >
                            <X className="h-3 w-3 cursor-pointer" />
                          </button>

                        </Badge>
                      ))
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
  
              <PopoverContent className="w-[340px] p-0">
                <Command>
                  <CommandInput placeholder="Search..." className={"outline-none"} />
                  <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup>
                      {options.map((opt) => {
                        const isActive = selected.includes(opt.value);
                        return (
                          <CommandItem
                            key={opt.value}
                            // set 'value' to label so Command's search matches on the human text
                            value={opt.label}
                            onSelect={() => toggle(opt.value)}
                            className="flex items-center justify-between"
                          >
                            <span className="whitespace-normal pr-2">{opt.label}</span>
                            {isActive && <Check className="h-4 w-4" />}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          );
        }}
      />
      <FieldError name={name} step={step}  />
    </div>
  );
};